import { fmt2 } from "@/lib/sqaafLabels";

export function buildSectionRingStyle(scorePct) {
  const pct = Math.min(100, Math.max(0, Number(scorePct) || 0));
  return {
    background: `radial-gradient(circle at center, #fff 0 54%, transparent 55%), conic-gradient(#8b5cf6 0 ${pct}%, rgba(139,92,246,.12) ${pct}% 100%)`,
    color: "#8b5cf6",
  };
}

export function getSectionPriority(mean, gap, coveragePct) {
  const m = Number(mean);
  const g = Number(gap);
  const cov = Number(coveragePct);
  if (m < 55 || cov < 25) return { label: "Critical", tag: "red" };
  if (g < 0) return { label: "Watch", tag: "amber" };
  return { label: "Healthy", tag: "green" };
}

export function mergeSectionRows(sectionAnalysis = {}) {
  const sorted = sectionAnalysis?.section_means_sorted || [];
  const summaryMap = new Map(
    (sectionAnalysis?.section_summary || []).map((r) => [r.section, r])
  );
  return sorted.map((row) => {
    const sm = summaryMap.get(row.section) || {};
    return {
      ...row,
      median: sm.median ?? row.median ?? row.mean,
      coverage_pct: sm["coverage_%"] ?? row.coverage_pct ?? 100,
      question_count: sm.question_count ?? row.question_count ?? 0,
    };
  });
}

export function filterSectionsByDomain(sections = [], domain = "All") {
  if (!domain || domain === "All Domains") return sections;
  const chapter = domain.startsWith("Domain 1") ? 1 : domain.startsWith("Domain 2") ? 2 : null;
  if (!chapter) return sections;
  return sections.filter((s) => String(s.section).startsWith(`${chapter}.`));
}

export function filterSectionsByCoverage(sections = [], coverage = "All Sections") {
  if (!coverage || coverage === "All Sections") return sections;
  if (coverage === "Low coverage") {
    return sections.filter((s) => Number(s.coverage_pct ?? 100) < 90);
  }
  return sections;
}

export function buildSectionRankRows(sections = [], districtMean = 0) {
  const marker = Math.min(100, Math.max(0, Number(districtMean) || 0));
  return sections.map((s) => ({
    section: s.section,
    label: s.section_label,
    mean: Number(s.mean ?? 0),
    width: Math.max(4, Number(s.mean ?? 0)),
    markerLeft: marker,
  }));
}

export function buildGapChartRows(sections = [], districtMean = 0) {
  const rows = sections.map((s) => {
    const gap = Number(s.gap ?? Number(s.mean ?? 0) - districtMean);
    return {
      section: s.section,
      label: s.section_label,
      gap,
      mean: Number(s.mean ?? 0),
    };
  });
  const maxAbs = Math.max(1, ...rows.map((r) => Math.abs(r.gap)));
  return rows.map((r) => ({
    ...r,
    direction: r.gap < 0 ? "down" : "up",
    height: Math.max(8, Math.round((Math.abs(r.gap) / maxAbs) * 120)),
    title: `${r.section} ${r.label}: ${r.gap >= 0 ? "+" : ""}${fmt2(r.gap)} pts`,
  }));
}

export function buildSectionHeroCopy(overview = {}, sections = [], districtMean = 0) {
  const count = sections.length;
  const critical = sections.filter((s) => Number(s.mean) < 55).length;
  const sorted = [...sections].sort((a, b) => (a.mean ?? 0) - (b.mean ?? 0));
  const lowest = sorted[0];
  const highest = sorted[sorted.length - 1];
  const meanScores = sections.map((s) => Number(s.mean ?? 0));
  const avgMean = meanScores.length
    ? meanScores.reduce((a, b) => a + b, 0) / meanScores.length
    : districtMean;
  const belowMean = sorted.filter((s) => Number(s.mean) < districtMean).length;
  const lowCov = sections.filter((s) => Number(s.coverage_pct) < 90);

  return {
    ringStyle: buildSectionRingStyle(avgMean),
    meanScore: fmt2(avgMean),
    headline: `${belowMean} of ${count} sections below district mean`,
    body: lowest && highest
      ? `Section ${lowest.section} (${fmt2(lowest.mean)}%) is weakest; section ${highest.section} (${fmt2(highest.mean)}%) is strongest vs ${fmt2(districtMean)}% district mean. ${critical} sections score below 55%${lowCov.length ? ` and ${lowCov.length} have coverage below 90%` : ""}.`
      : `Tracking ${count} sections against ${fmt2(districtMean)}% district mean.`,
    chips: [
      { label: `${count} Sections Tracked`, className: "purple" },
      { label: `District Mean ${fmt2(districtMean)}`, className: "blue" },
      { label: `${critical} Critical Sections`, className: "red" },
    ],
    actionNote: `Focus remediation on sections below ${fmt2(districtMean)}, especially section ${lowest?.section || "—"} (${fmt2(lowest?.mean)}%)${lowCov[0] ? ` and low-coverage section ${lowCov[0].section} (${fmt2(lowCov[0].coverage_pct)}%)` : ""}. Use section ${highest?.section || "—"} (${fmt2(highest?.mean)}%) as an internal exemplar.`,
  };
}

export function buildSectionHeroMetrics(overview = {}, sections = [], districtMean = 0) {
  const sorted = [...sections].sort((a, b) => (a.mean ?? 0) - (b.mean ?? 0));
  const lowest = sorted[0];
  const highest = sorted[sorted.length - 1];
  const means = sorted.map((s) => Number(s.mean ?? 0));
  const median = means.length ? means[Math.floor(means.length / 2)] : 0;
  const belowMean = sorted.filter((s) => Number(s.mean) < districtMean).length;
  const critical = sorted.filter((s) => Number(s.mean) < 55).length;

  return [
    { label: "Highest Section", value: highest?.section || "—", sub: `${highest?.section_label || ""} · ${fmt2(highest?.mean)}%`, badge: "green", icon: "up" },
    { label: "Lowest Section", value: lowest?.section || "—", sub: `${lowest?.section_label || ""} · ${fmt2(lowest?.mean)}%`, badge: "red", icon: "down" },
    { label: "Median Score", value: fmt2(median), sub: "Balanced middle point", badge: "purple", icon: "dot" },
    { label: "Below District Mean", value: String(belowMean), sub: `Sections below ${fmt2(districtMean)}`, badge: "orange", icon: "bang" },
    { label: "Coverage Rate", value: `${fmt2(overview?.completion_rate)}%`, sub: "Mean answered %", badge: "", icon: "check" },
    { label: "Critical Sections", value: String(critical), sub: "Scores below 55%", badge: "red", icon: "warn" },
  ];
}

export function buildSectionInsightCards(sections = [], districtMean = 0) {
  const sorted = [...sections].sort((a, b) => (a.mean ?? 0) - (b.mean ?? 0));
  const bottom = sorted.slice(0, 3);
  const top = sorted.slice(-3).reverse();
  const bottomDetail = bottom.map((s) => `section ${s.section} (${fmt2(s.mean)}%)`).join(", ");
  const topDetail = top.map((s) => `section ${s.section} (${fmt2(s.mean)}%)`).join(", ");
  const lowCov = sections.filter((s) => Number(s.coverage_pct) < 90).length;

  return [
    {
      tone: "red",
      title: "Risk Cluster",
      body: `${bottomDetail || "Low-scoring sections"} trail the ${fmt2(districtMean)}% district mean — primary risk zone for corrective action planning.`,
    },
    {
      tone: "green",
      title: "Strength Anchor",
      body: `${topDetail || "Top sections"} exceed the district mean and can serve as best-practice anchors for peer blocks.`,
    },
    {
      tone: "orange",
      title: "Recommended Next Step",
      body: lowCov
        ? `Review ${lowCov} low-coverage sections alongside ${bottom.length} lowest-scoring sections in the next block review.`
        : `Assign improvement owners to the ${bottom.length} lowest sections in the next block review.`,
    },
  ];
}

export function buildSectionTableRows(sections = [], districtMean = 0) {
  return sections.map((s) => {
    const gap = Number(s.gap ?? Number(s.mean ?? 0) - districtMean);
    const priority = getSectionPriority(s.mean, gap, s.coverage_pct);
    return {
      section: s.section,
      section_label: s.section_label,
      question_count: s.question_count ?? 0,
      coverage_pct: `${fmt2(s.coverage_pct ?? 100)}%`,
      mean: fmt2(s.mean),
      median: fmt2(s.median ?? s.mean),
      gap: gap >= 0 ? `+${fmt2(gap)}` : fmt2(gap),
      gapClass: gap >= 0 ? "score-good" : "score-bad",
      priority: priority.label,
      priorityTag: priority.tag,
    };
  });
}

export function buildFocusList(sections = [], kind = "low", count = 4) {
  const sorted = [...sections].sort((a, b) =>
    kind === "low" ? (a.mean ?? 0) - (b.mean ?? 0) : (b.mean ?? 0) - (a.mean ?? 0)
  );
  return sorted.slice(0, count).map((s) => ({
    section: s.section,
    mean: fmt2(s.mean),
  }));
}
